import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { first, map, switchMap, tap, withLatestFrom } from "rxjs";
import { TenantApiService } from "app/api/tenant.api.service";
import { Store } from "@ngrx/store";

import * as TenantActions from "../actions/tenant.actions";
import * as fromTenant from "../reducers";
import { SearchOptions } from "@neccton-gmbh/client-commons";
import { MessageService } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class TenantEffects {
    constructor(
        private actions$: Actions,
        private api: TenantApiService,
        private store: Store,
        private messageService: MessageService,
        private translateService: TranslateService,
    ) {}

    getTenantInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantActions.getTenantInfo),
            switchMap(() => {
                return this.api.getTenantInfo().pipe(
                    first(),
                    map((tenant) =>
                        TenantActions.getTenantInfoSuccess({ tenant }),
                    ),
                );
            }),
        ),
    );

    getTenantConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantActions.getTenantConfig),
            switchMap(() => {
                return this.api.getTenantConfig().pipe(
                    first(),
                    map((config) =>
                        TenantActions.getTenantConfigSuccess({ config }),
                    ),
                );
            }),
        ),
    );

    setTenantConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantActions.setTenantConfig),
            withLatestFrom(this.store.select(fromTenant.getTenantConfig)),
            switchMap(([{ config }, currentConfig]) => {
                return this.api
                    .setTenantConfig({ ...currentConfig, ...config })
                    .pipe(
                        first(),
                        tap(() => {
                            this.messageService.add({
                                severity: "success",
                                summary:
                                    this.translateService.instant(
                                        "APP.ACTION.SUCCESS",
                                    ),
                                detail: this.translateService.instant(
                                    "APP.TENANT.SETTINGS.CHANGED_SUCCESSFULLY",
                                ),
                            });
                        }),
                        map((config) =>
                            TenantActions.setTenantConfigSuccess({ config }),
                        ),
                    );
            }),
        ),
    );

    updateTenantPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantActions.updateTenantPhoto),
            switchMap(({ photo }) =>
                this.api.updateTenantPhoto(photo).pipe(
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.HEADER.SETTINGS_DROPDOWN.ACTIONS.UPDATE_USER_PHOTO.SUMMARY",
                            ),
                        });
                    }),
                    map((response) =>
                        TenantActions.updateTenantPhotoSuccess({ response }),
                    ),
                ),
            ),
        ),
    );

    deleteTenantPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantActions.deleteTenantPhoto),
            switchMap(() =>
                this.api.deleteTenantPhoto().pipe(
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.HEADER.SETTINGS_DROPDOWN.ACTIONS.DELETE_USER_PHOTO.SUMMARY",
                            ),
                        });
                    }),
                    map(() => TenantActions.deleteTenantPhotoSuccess()),
                ),
            ),
        ),
    );
}
